import React from 'react';
import { Row, Col, Alert, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { isPwaStandalone } from '../utils/pwaStandalone';
import PwaInstallButton from './PwaInstallButton';
import XpPotionBonusResultLine from './XpPotionBonusResultLine';
import type { QuizAttemptResponse } from '../services/api';

export interface SoloQuizResultsBodyProps {
  hasTooManySkips: boolean;
  isQuizCompleted: boolean;
  finishedAsGameOver?: boolean;
  quizTitleDisplay: string;
  correctAnswers: number;
  totalQuestions: number;
  score: number;
  earnedXP: number;
  quizResponse: QuizAttemptResponse | null;
  resultDurationSeconds: number | null;
  /** When true, omits the “install app for +15% XP” info alert (e.g. PvP host follow-up). */
  hideInstallAppTeaser?: boolean;
  /** Solo MC: accuracy ≥ 70% — offer to list same run on Arena PvP. */
  arenaPublishOffer?: boolean;
  arenaPublishChecked?: boolean;
  onArenaPublishCheckedChange?: (checked: boolean) => void;
  /** True while checking hosted-open summary; keeps publish option disabled. */
  arenaPublishAvailabilityLoading?: boolean;
  /** Hosted-open rules: duplicate quiz and/or max open Arena rooms. */
  arenaPublishBlocked?: boolean;
  arenaPublishBlockedReason?: 'duplicateQuiz' | 'maxRooms';
  arenaPublishMaxOpenRooms?: number;
}

/**
 * Shared results content: stats grid, XP breakdown, speed/VIP/app — used by
 * {@link SoloQuizResultsModal} and PvP async host completion.
 */
const SoloQuizResultsBody: React.FC<SoloQuizResultsBodyProps> = ({
  hasTooManySkips,
  isQuizCompleted,
  finishedAsGameOver = false,
  quizTitleDisplay,
  correctAnswers,
  totalQuestions,
  score,
  earnedXP,
  quizResponse,
  resultDurationSeconds,
  hideInstallAppTeaser = false,
  arenaPublishOffer = false,
  arenaPublishChecked = true,
  onArenaPublishCheckedChange,
  arenaPublishAvailabilityLoading = false,
  arenaPublishBlocked = false,
  arenaPublishBlockedReason,
  arenaPublishMaxOpenRooms,
}) => {
  const { t } = useSafeTranslation();

  return (
    <div className="solo-quiz-results-body text-center">
      {hasTooManySkips ? (
        <>
          <h2>{t('quiz.tooManySkipped', 'Too Many Questions Skipped')}</h2>
          <p>
            {t('quiz.quizSkippedMessage', 'You completed the quiz: {{title}}').replace('{{title}}', quizTitleDisplay)}
          </p>
        </>
      ) : isQuizCompleted ? (
        finishedAsGameOver ? (
          <>
            <h2>{t('quiz.gameOverResults', 'Game Over')}</h2>
            <p>
              {t('quiz.gameOverResultsMessage', 'Your run ended: {{title}}').replace(
                '{{title}}',
                quizTitleDisplay
              )}
            </p>
          </>
        ) : (
          <>
            <h2>{t('quiz.congratulations', 'Congratulations!')}</h2>
            <p>
              {t('quiz.completedQuiz', 'You completed the quiz: {{title}}').replace('{{title}}', quizTitleDisplay)}
            </p>
          </>
        )
      ) : (
        <>
          <h2>{t('quiz.quizAbandoned', 'Quiz Abandoned')}</h2>
          <p>
            {t('quiz.quitQuizMessage', 'You quit the quiz: {{title}}').replace('{{title}}', quizTitleDisplay)}
          </p>
        </>
      )}

      <div className="results-stats quiz-results-stats-grid solo-quiz-results-stat-grid">
        <Row className="g-1 w-100 m-0">
          <Col xs={6} className="p-0 pe-1">
            <div className="stat-item h-100">
              <h3>{correctAnswers}</h3>
              <p>{t('quiz.correctAnswers', 'Correct Answers')}</p>
            </div>
          </Col>
          <Col xs={6} className="p-0 ps-1">
            <div className="stat-item h-100">
              <h3>{totalQuestions}</h3>
              <p>{t('quiz.totalQuestionsLabel', 'Total Questions')}</p>
            </div>
          </Col>
          <Col xs={6} className="p-0 pe-1">
            <div className="stat-item h-100">
              <h3>{totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0}%</h3>
              <p>{t('quiz.accuracyLabel', 'Accuracy')}</p>
            </div>
          </Col>
          <Col xs={6} className="p-0 ps-1">
            <div className="stat-item h-100">
              <h3>{score}</h3>
              <p>{t('quiz.scoreLabel', 'Score')}</p>
            </div>
          </Col>
        </Row>
      </div>

      {hasTooManySkips ? (
        <div className="xp-earned-section quiz-abandoned">
          <div className="xp-earned-badge">
            <i className="bi bi-x-circle text-muted me-2"></i>
            <span className="xp-earned-text text-muted">{t('quiz.noXPEarned', 'No XP Earned')}</span>
          </div>
          <div className="xp-breakdown">
            <small className="text-muted">
              {t('quiz.noXPSkipped', 'No XP earned - too many questions were skipped')}
            </small>
          </div>
        </div>
      ) : isQuizCompleted ? (
        <div className="xp-earned-section">
          <div className="xp-earned-badge">
            <i className="bi bi-star-fill text-warning me-2"></i>
            <span className="xp-earned-text">
              {t('quiz.xpEarned', '+{{xp}} XP Earned!').replace('{{xp}}', String(earnedXP))}
            </span>
          </div>
          <div className="xp-breakdown mt-2">
            <small className="text-muted">
              {quizResponse?.baseXP &&
              quizResponse?.bonusXPPerCorrectAnswer &&
              quizResponse?.totalBonusXP &&
              quizResponse?.difficulty &&
              quizResponse?.correctAnswers != null ? (
                <>
                  {t('quiz.baseXP', 'Base XP: +{{base}}').replace('{{base}}', String(quizResponse.baseXP))} |{' '}
                  {t('quiz.bonusXP', 'Bonus XP: +{{bonus}} ({{correct}} correct × {{perCorrect}} per correct)')
                    .replace('{{bonus}}', String(quizResponse.totalBonusXP))
                    .replace('{{correct}}', String(quizResponse.correctAnswers))
                    .replace('{{perCorrect}}', String(quizResponse.bonusXPPerCorrectAnswer))}
                </>
              ) : (
                t('quiz.xpCalculated', 'XP calculated by server based on difficulty and performance')
              )}
            </small>
            {quizResponse?.speedBonusXp != null && (
              <div className="mt-1">
                <small className="text-info">
                  <i className="bi bi-lightning-charge-fill me-1" aria-hidden />
                  {t('quiz.speedBonusXP', 'Speed bonus: +{{xp}} XP').replace('{{xp}}', String(quizResponse.speedBonusXp))}
                </small>
              </div>
            )}
            {quizResponse?.soloXpPotionBonusAmount != null && quizResponse.soloXpPotionBonusAmount > 0 && (
              <XpPotionBonusResultLine
                bonusAmount={quizResponse.soloXpPotionBonusAmount}
                bonusPercent={quizResponse.soloXpPotionBonusPercent ?? 0}
              />
            )}
            {quizResponse?.soloVipXpBonusAmount != null && quizResponse.soloVipXpBonusAmount > 0 && (
              <div className="mt-1">
                <small className="d-inline-flex align-items-center flex-wrap gap-1 vip-subscriber-nickname">
                  <i className="bi bi-gem text-warning flex-shrink-0" title="VIP" aria-hidden />
                  <span>
                    {t('quiz.vipXpBonusXP', 'VIP bonus: +{{xp}} XP').replace(
                      '{{xp}}',
                      String(quizResponse.soloVipXpBonusAmount)
                    )}
                  </span>
                </small>
              </div>
            )}
            {quizResponse?.pvpArenaExtraXp != null && quizResponse.pvpArenaExtraXp > 0 && (
              <div className="mt-1">
                <small className="text-primary">
                  <i className="bi bi-lightning-fill me-1" aria-hidden />
                  {t('pvp.arenaXpBonusLine', 'Arena PvP: +{{xp}} XP').replace(
                    '{{xp}}',
                    String(quizResponse.pvpArenaExtraXp)
                  )}
                </small>
              </div>
            )}
          </div>
          {quizResponse?.soloAppXpBonusApplied && (
            <Alert
              variant="success"
              className="mt-3 mb-0 text-center quiz-results-app-xp-alert d-flex flex-column align-items-center"
            >
              <div className="fw-semibold d-flex justify-content-center align-items-center flex-wrap gap-1">
                <i className="bi bi-download" aria-hidden />
                <span>
                  {t('quiz.appXpBonusHeadline', 'App XP bonus +{{percent}}% · +{{amount}} XP')
                    .replace('{{percent}}', String(quizResponse.soloAppXpBonusPercent ?? 15))
                    .replace('{{amount}}', String(quizResponse.soloAppXpBonusAmount ?? 0))}
                </span>
              </div>
              <small className="d-block mt-1 text-success-emphasis text-center w-100">
                {t('quiz.appXpBonusSubline', 'Extra XP for playing in the installed app.')}
              </small>
            </Alert>
          )}
          {!quizResponse?.soloAppXpBonusApplied &&
            (earnedXP ?? 0) > 0 &&
            !isPwaStandalone() &&
            !hideInstallAppTeaser && (
              <Alert
                variant="info"
                className="mt-3 mb-0 text-center quiz-results-install-app-alert d-flex flex-column align-items-center"
              >
                <div className="mb-2">{t('quiz.installAppXpTeaser', 'Earn 15% more XP by playing in the app.')}</div>
                <PwaInstallButton />
              </Alert>
            )}
        </div>
      ) : (
        <div className="xp-earned-section quiz-abandoned">
          <div className="xp-earned-badge">
            <i className="bi bi-x-circle text-muted me-2"></i>
            <span className="xp-earned-text text-muted">{t('quiz.noXPEarned', 'No XP Earned')}</span>
          </div>
          <div className="xp-breakdown">
            <small className="text-muted">
              {t('quiz.noXPAbandoned', 'No XP earned - quiz was not completed')}
            </small>
          </div>
        </div>
      )}

      {resultDurationSeconds !== null && (
        <p className="text-muted">
          {t('quiz.timeTaken', 'Time taken: {{seconds}} seconds').replace('{{seconds}}', String(resultDurationSeconds))}
        </p>
      )}
      {arenaPublishOffer && onArenaPublishCheckedChange && isQuizCompleted && !hasTooManySkips && (
        <>
          {(() => {
            const disabled =
              arenaPublishAvailabilityLoading || arenaPublishBlocked;
            const block = (
              <div className="mt-3">
                <div className="small text-center mb-2">
                  {t(
                    'quiz.soloArenaPublishHeadline',
                    'Amazing score! This result will be published as a room on the PvP Arena.'
                  )}
                </div>
                <div className="d-flex justify-content-center align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="solo-quiz-publish-arena-checkbox"
                    className="mb-0 small"
                    disabled={disabled}
                    checked={arenaPublishBlocked ? false : arenaPublishChecked}
                    onChange={(e) => {
                      if (!disabled) onArenaPublishCheckedChange(e.target.checked);
                    }}
                    label={
                      <span
                        className="text-muted"
                        style={{ fontSize: '0.75rem', lineHeight: 1.35 }}
                      >
                        {t(
                          'quiz.soloArenaPublishDisclaimer',
                          'Uncheck to skip automatic publishing.'
                        )}
                      </span>
                    }
                  />
                </div>
              </div>
            );
            if (arenaPublishBlocked && !arenaPublishAvailabilityLoading) {
              const maxRooms = arenaPublishMaxOpenRooms ?? 3;
              const tooltipBody =
                arenaPublishBlockedReason === 'maxRooms'
                  ? t(
                      'quiz.soloArenaPublishBlockedMaxRoomsTooltip',
                      "You've reached the limit of {{max}} open Arena rooms. Finish a match or wait before this run can be published automatically."
                    ).replace(/\{\{max\}\}/g, String(maxRooms))
                  : t(
                      'quiz.soloArenaPublishBlockedTooltip',
                      'You already have an open Arena room for this quiz. You cannot publish this run until that room is no longer open.'
                    );
              const tip = (
                <Tooltip id="solo-quiz-publish-arena-blocked-tip">
                  {tooltipBody}
                </Tooltip>
              );
              return (
                <OverlayTrigger placement="top" trigger={['hover', 'focus']} overlay={tip}>
                  <span className="d-inline-block mw-100">{block}</span>
                </OverlayTrigger>
              );
            }
            return block;
          })()}
        </>
      )}
    </div>
  );
};

export default SoloQuizResultsBody;
